
import { useState } from 'react';

interface Color {
  name: string;
  hex: string;
}

interface Variant {
  name: string;
  description: string;
  priceAdjust: number;
}

interface ProductInfoProps {
  name: string;
  tagline: string;
  category: string;
  price: number;
  originalPrice: number;
  description: string;
  colors: Color[];
  variants: Variant[];
  specs: {
    motor: string;
    range: string;
    topSpeed: string;
    battery: string;
    chargeTime: string;
    weight: string;
  };
  rating: number;
  reviewCount: number;
}

export default function ProductInfo({
  name,
  tagline,
  category,
  price,
  originalPrice,
  description,
  colors,
  variants,
  specs,
  rating,
  reviewCount,
}: ProductInfoProps) {
  const [selectedColor, setSelectedColor] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  const activePrice = price + (variants[selectedVariant]?.priceAdjust || 0);
  const activeOriginalPrice = originalPrice + (variants[selectedVariant]?.priceAdjust || 0);
  const discount = Math.round(((activeOriginalPrice - activePrice) / activeOriginalPrice) * 100);

  const handleAddToCart = () => {
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const quickSpecs = [
    { icon: 'ri-flashlight-line', label: 'Motor', value: specs.motor },
    { icon: 'ri-route-line', label: 'Range', value: specs.range },
    { icon: 'ri-speed-line', label: 'Top Speed', value: specs.topSpeed },
    { icon: 'ri-battery-charge-line', label: 'Battery', value: specs.battery },
    { icon: 'ri-time-line', label: 'Charge', value: specs.chargeTime },
    { icon: 'ri-scales-line', label: 'Weight', value: specs.weight },
  ];

  return (
    <div className="flex flex-col">
      <span className="inline-block bg-teal-500/15 text-teal-400 text-xs font-semibold px-3 py-1 rounded-full mb-3 w-fit">
        {category}
      </span>

      <h1 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight mb-1">{name}</h1>
      <p className="text-lg text-gray-400 mb-4">{tagline}</p>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <i
              key={i}
              className={`ri-star-${i < Math.floor(rating) ? 'fill' : i < rating ? 'half-fill' : 'line'} text-amber-400 text-sm`}
            ></i>
          ))}
        </div>
        <span className="text-sm text-gray-400">
          {rating} ({reviewCount} reviews)
        </span>
      </div>

      <div className="flex items-baseline gap-3 mb-6">
        <span className="text-3xl font-black">${activePrice.toLocaleString()}</span>
        <span className="text-lg text-gray-500 line-through">${activeOriginalPrice.toLocaleString()}</span>
        <span className="bg-red-500/20 text-red-400 text-xs font-bold px-2 py-1 rounded-full">
          -{discount}%
        </span>
      </div>

      <p className="text-sm text-gray-300 leading-relaxed mb-8">{description}</p>

      {variants.length > 0 && (
        <div className="mb-8">
          <div className="text-sm font-semibold mb-3">
            VARIANT — <span className="text-gray-400 font-normal">{variants[selectedVariant].name}</span>
          </div>
          <div className="flex flex-col gap-2">
            {variants.map((variant, index) => (
              <button
                key={variant.name}
                onClick={() => setSelectedVariant(index)}
                className={`relative flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all cursor-pointer text-left ${
                  selectedVariant === index
                    ? 'border-teal-500 bg-teal-500/10'
                    : 'border-zinc-700 bg-zinc-900 hover:border-zinc-500'
                }`}
              >
                <div className="flex flex-col">
                  <span className={`text-sm font-bold ${selectedVariant === index ? 'text-teal-400' : 'text-white'}`}>
                    {variant.name}
                  </span>
                  <span className="text-xs text-gray-400 mt-0.5">{variant.description}</span>
                </div>
                <div className="flex items-center gap-2">
                  {variant.priceAdjust > 0 && (
                    <span className="text-xs text-gray-400">+${variant.priceAdjust}</span>
                  )}
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      selectedVariant === index
                        ? 'border-teal-500'
                        : 'border-zinc-600'
                    }`}
                  >
                    {selectedVariant === index && (
                      <div className="w-2.5 h-2.5 rounded-full bg-teal-500"></div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
        {quickSpecs.map((spec) => (
          <div key={spec.label} className="bg-zinc-900 rounded-lg p-3 text-center">
            <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
              <i className={`${spec.icon} text-teal-400 text-lg`}></i>
            </div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wider">{spec.label}</div>
            <div className="text-xs font-bold mt-0.5">{spec.value}</div>
          </div>
        ))}
      </div>

      <div className="mb-6">
        <div className="text-sm font-semibold mb-3">
          COLOR — <span className="text-gray-400 font-normal">{colors[selectedColor].name}</span>
        </div>
        <div className="flex gap-3">
          {colors.map((color, index) => (
            <button
              key={color.name}
              onClick={() => setSelectedColor(index)}
              className={`w-10 h-10 rounded-full cursor-pointer transition-all ${
                selectedColor === index
                  ? 'ring-2 ring-teal-500 ring-offset-2 ring-offset-black'
                  : 'ring-1 ring-zinc-600 hover:ring-zinc-400'
              }`}
              style={{ backgroundColor: color.hex }}
              title={color.name}
            />
          ))}
        </div>
      </div>

      <div className="mb-6">
        <div className="text-sm font-semibold mb-3">QUANTITY</div>
        <div className="flex items-center gap-0 bg-zinc-900 rounded-lg w-fit">
          <button
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            className="w-10 h-10 flex items-center justify-center hover:bg-zinc-800 rounded-l-lg transition-colors cursor-pointer"
          >
            <i className="ri-subtract-line text-sm"></i>
          </button>
          <span className="w-12 text-center text-sm font-bold">{quantity}</span>
          <button
            onClick={() => setQuantity((q) => q + 1)}
            className="w-10 h-10 flex items-center justify-center hover:bg-zinc-800 rounded-r-lg transition-colors cursor-pointer"
          >
            <i className="ri-add-line text-sm"></i>
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <button
          onClick={handleAddToCart}
          className={`w-full py-4 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
            addedToCart
              ? 'bg-green-500 text-black'
              : 'bg-teal-500 text-black hover:bg-teal-400 hover:scale-[1.02]'
          }`}
        >
          {addedToCart ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-check-line text-lg"></i> ADDED TO CART
            </span>
          ) : (
            `ADD TO CART — $${(activePrice * quantity).toLocaleString()}`
          )}
        </button>
        <div className="grid grid-cols-2 gap-3">
          <button className="border-2 border-zinc-700 text-white py-3 rounded-full text-sm font-bold hover:border-teal-500 hover:text-teal-400 transition-all cursor-pointer whitespace-nowrap">
            REQUEST QUOTE
          </button>
          <button className="border-2 border-zinc-700 text-white py-3 rounded-full text-sm font-bold hover:border-teal-500 hover:text-teal-400 transition-all cursor-pointer whitespace-nowrap">
            SCHEDULE DEMO
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 md:gap-6 mt-6 pt-6 border-t border-zinc-800">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <i className="ri-truck-line text-teal-400"></i>
          Free Shipping
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <i className="ri-shield-check-line text-teal-400"></i>
          2-Year Warranty
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <i className="ri-refresh-line text-teal-400"></i>
          30-Day Returns
        </div>
      </div>
    </div>
  );
}
